import { channel, Channel } from 'redux-saga'
import { call, put, take, select } from 'redux-saga/effects'

import { AppState } from 'containers/store'

import { actionCreators } from 'domains/account/actions'
import { AuthSession, authSessionSelector } from 'domains/account/auth'
import { authStorageKey } from 'domains/account/auth-session'

import { Codec, RemoteObject } from 'generated/webrpc'
import { UnaryRequest, UnaryResponse } from 'generated/mos/admin/webrpc'
import { RefreshAuthenticationTokenRequest } from 'generated/mos/userauthentication'

import { bffEndpoint, StatusCode } from 'helpers/bff'
import { Invariant } from 'helpers/core'
import { captureException } from 'helpers/logging'
import { Message, Status } from 'helpers/status'

export const grpcEndpoint = `${bffEndpoint(window.location.hostname)}/call`

export const ensureSessionChannel: Channel<any> = channel();

type ErrorCode = string;
export type ErrorMessage = Message<ErrorCode>;

export const failureStatusMessage = (message: string): ErrorMessage => {
  captureException({ error: message })
  return {
    code: message,
    text: message,
  }
}

// only exported for integration with thunks for s3 integration while that's still a thing.
export function* ensureValidSession(): any { // FIXME: any
  const state: AppState['account'] = yield select((store: AppState) => store.account)
  const session = state && state.session
  const tokenStatus = state && state.tokenStatus

  if(!session) {
    throw new Error('No session data available.')
  }

  // is the current refresh token expired?
  else if (authSessionSelector.refreshExpired(session)) {
    // we can't do much with this user. log them out so they can retrieve new tokens.
    yield put(actionCreators.accountLogout())
  }

  // is the current access token expired?
  else if (authSessionSelector.accessExpired(session)) {
    // is there already a token refresh request in-flight?
    if (tokenStatus.status === Status.Updating) {
      // pause for the refresh, then allow channel to continue.
      yield take(ensureSessionChannel)
      yield put(ensureSessionChannel, {})

    // otherwise exchange the refresh token for a new token pair.
    } else {
      // request and wait for token refresh and storage update.
      yield put(actionCreators.accountRefreshAuthenticationTokenRequest())
      yield take('accountRefreshAuthenticationTokenSuccess')

      // allow channel to continue.
      yield take(ensureSessionChannel)
      yield put(ensureSessionChannel, {})
    }

    const { account: updatedAccount } = yield select()
    return updatedAccount.session as AuthSession
  }

  // we already have a valid token.
  else if (tokenStatus.status === Status.Ready) {
    return session
  }

  throw new Invariant('Unexpected token refresh status.')
}

export const assemblePayload = (requestBodyObject: RemoteObject) => {
  // retrieve access token if available.
  const tokenStorage = localStorage.getItem(authStorageKey);
  const tokens = tokenStorage ? JSON.parse(tokenStorage) : false;

  // set any standard headers for grpc calls.
  const headers = new Headers()
  headers.append('Content-Type', 'application/json')
  if (tokens && tokens.access) {
    headers.append('Authorization', `bearer ${tokens.access}`)
  }

  // encode the request body.
  const requestPayload = {
    ...UnaryRequest.defaults,
    body: requestBodyObject,
  }
  const encodedPayload = UnaryRequest.codec.encode(requestPayload)

  // assemble the payload.
  return {
    method: 'POST',
    headers,
    body: JSON.stringify(encodedPayload)
  }
}

export class UnaryGRPCError extends Error {
  public name: string

  public constructor(message?: string) {
    super(message)
    this.name = new.target.prototype.constructor.name
    Object.setPrototypeOf(this, new.target.prototype)
  }
}

type Entity = {
  type: string;
}

export function* unaryGRPC<TRequestEntity extends Entity, TResponseEntity extends Entity>(
  grpcPath: string,
  requestEntity: TRequestEntity,
  requestCodec: Codec<TRequestEntity>,
  responseCodec: Codec<TResponseEntity>,
) {
  // pull the session out of the store so we can inspect authentication tokens.
  const session: AppState['account']['session'] = yield select((store: AppState) => store.account.session)

  // check the tokens are valid, as long as this is not a token refresh request which would
  // would recursively stall the channel.
  if (
    session &&
    (authSessionSelector.accessExpired(session) || authSessionSelector.refreshExpired(session)) &&
    requestEntity.type !== RefreshAuthenticationTokenRequest.refName
  ) {
    // refresh access token or queue request until a new token is obtained.
    yield call(ensureValidSession)
  }

  // encode and assemble the payload.
  const encodedPayload = requestCodec.encode(requestEntity)
  let payload = assemblePayload(encodedPayload)

  // attempt to make the network request, or terminate on error.
  let result: Response
  try {
    result = yield call(fetch, `${grpcEndpoint}/${grpcPath}`, payload)
  } catch (error) {
    throw new UnaryGRPCError(`[ UnaryGRPC ] There was a network error: ${error.message}.`)
  }

  // check for errors in the response.
  if (!result || !result.ok) {
    const data: RemoteObject = yield call([result, 'json'])
    const response = UnaryResponse.codec.decode(data)
    if (!response) {
      throw new UnaryGRPCError('[ UnaryGRPC ] There was an error with receiving an expected response.')
    }

    // check for invalid access token. this block is legacy code and in theory should never be hit
    // because we preemptively refresh tokens above. leaving this as a failsafe because sentry
    // should record the error at this point.
    else if (response.code && response.code === StatusCode.PermissionDenied) {
      // refresh access token or queue request until a new token is obtained.
      yield call(ensureValidSession)

      // once ready, retry the network request a second and final time.
      try {
        payload = assemblePayload(encodedPayload)
        result = yield call(fetch, `${grpcEndpoint}/${grpcPath}`, payload)
      } catch (error) {
        throw new UnaryGRPCError(`[ UnaryGRPC ] There was a network error: ${error.message}.`)
      }

      if (!result || !result.ok) {
        throw new UnaryGRPCError('[ UnaryGRPC ] There was an error with the response.')
      }

    }
    // TODO: check what the code is and provide a more meaningful error to the ui.
    else if ((response.code && response.code > 0) || response.errors) {
      throw new UnaryGRPCError(`[ UnaryGRPC ] ${response.message || 'An error was received.'}`)
    } else {
      throw new Invariant('[ UnaryGRPC ] Unhandled response.')
    }
  }

  // if we get this far the response was successful, now get data and check it matches generated
  //   types.
  const rawResponse: RemoteObject = yield call([result, 'json'])
  const data = {
    ...UnaryResponse.defaults,
    ...rawResponse,
  }
  const response = UnaryResponse.codec.decode(data)

  if (!response || !response.body) {
    throw new UnaryGRPCError('[ UnaryGRPC ] There was an error with receiving an expected response.')
  }
  // TODO: check what the code is and provide a more meaningful error to the ui.
  else if ((response.code && response.code > 0) || response.errors) {
    throw new UnaryGRPCError(`[ UnaryGRPC ] ${response.message || 'An error was received.'}`)
  }

  // check for validation failures, terminate on error or return the validated payload to the
  //   parent generator.
  const validated: TResponseEntity = responseCodec.decode(response.body)
  if (!validated) {
    throw new UnaryGRPCError('[ UnaryGRPC ] Response payload shape validation failed.')
  }

  return validated
}
